import { defaultTheme, parseThemeName, type ThemeName } from "./themes";

/** The browser key the theme is cached under — a first-paint cache only since KEY-755; the account holds the choice. */
export const THEME_CACHE_KEY = "kdu.theme";

/** Just enough of `Storage` to read and write the cache, so tests can hand in a plain object. */
export interface ThemeCache {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Where a signed-in person's theme lives: `GET` / `PUT /api/me/theme`. */
export interface ThemeStore {
  /** The stored theme, or the server's whole answer: the stored theme plus the themes offered here. */
  load(): Promise<string | null | { theme: string | null; available?: readonly string[] | null }>;
  save(theme: ThemeName): Promise<unknown>;
}

/**
 * The theme to paint before anything has been asked of the server: the cached choice if it is still a known
 * theme, else the OS preference. Never throws — a blocked or absent storage reads as "nothing cached".
 */
export function readCachedTheme(cache: ThemeCache | null | undefined, prefersDark: boolean): ThemeName {
  let stored: string | null = null;
  try {
    stored = cache?.getItem(THEME_CACHE_KEY) ?? null;
  } catch {
    // private mode / storage disabled
  }
  return parseThemeName(stored) ?? defaultTheme(prefersDark);
}

export function writeCachedTheme(cache: ThemeCache | null | undefined, theme: ThemeName): void {
  try {
    cache?.setItem(THEME_CACHE_KEY, theme);
  } catch {
    // private mode / storage disabled — keep the in-memory choice
  }
}

/**
 * Keeps the theme on screen in step with the signed-in person's stored one (KEY-755).
 *
 * - `bind(store)` on sign-in (or a PIN unlock — the tablet takes the unlocking person's theme): reads their
 *   stored theme and applies it. Nothing stored leaves the current theme alone; it is NOT pushed up, because on
 *   a shared tablet the current theme is the previous person's, and adopting it would make their choice ours.
 * - `pick(theme)` is the person choosing: applied at once, cached, and saved when an account is bound.
 * - `unbind()` on sign-out: the theme stays as it is (cached), and nothing more is saved.
 *
 * A pick made while the sign-in read is still in flight wins: the late answer is the older fact.
 */
export class ThemeSync {
  private store: ThemeStore | null = null;
  /** Bumped on every bind/unbind/pick, so an answer that arrives after any of them is recognised as stale. */
  private generation = 0;

  constructor(
    private readonly apply: (theme: ThemeName) => void,
    private readonly cache: ThemeCache | null | undefined,
    /** Told which themes the server offers (null on sign-out: back to the finished themes only). */
    private readonly offer: (available: readonly string[] | null) => void = () => {},
  ) {}

  async bind(store: ThemeStore): Promise<void> {
    this.store = store;
    const generation = ++this.generation;
    let stored: string | null;
    try {
      const answer = await store.load();
      if (answer !== null && typeof answer === "object") {
        if (generation === this.generation && answer.available) this.offer(answer.available);
        stored = answer.theme;
      } else {
        stored = answer;
      }
    } catch {
      // An API older than this build, or a refusal: keep the device's theme rather than flicker to a default.
      return;
    }
    if (generation !== this.generation) return;
    const theme = parseThemeName(stored);
    if (theme) {
      this.apply(theme);
      writeCachedTheme(this.cache, theme);
    }
  }

  unbind(): void {
    this.store = null;
    this.generation++;
    this.offer(null);
  }

  async pick(theme: ThemeName): Promise<void> {
    this.generation++;
    this.apply(theme);
    writeCachedTheme(this.cache, theme);
    const store = this.store;
    if (!store) return;
    try {
      await store.save(theme);
    } catch {
      // A display preference: the choice stands on this device even if the server couldn't keep it.
    }
  }
}
